import { call, put, takeEvery, takeLatest, all, select } from 'redux-saga/effects';
import { IRequestParams, IResponse } from '@dash/apiInstance';
import { t } from 'src/i18n';
import { sendRequest } from '../../../helpers/saga';
import { logger } from '../../../helpers/logger';
import ApiInstance from '../../../helpers/apiInstance';
import { UserRoles } from '../../../helpers/userRoles';
import actionTypes from './actionTypes';
import notifications from '../../../redux/notifications/actions';
import {
  ILoadAdminManagerSuccessPayload,
  ILoadSettingsResponse,
  IUpdateSettingsResponse,
  IUpdateSalesDemoSettingsRequestPayload,
  ILoadInvoiceSortingRulesFormDataSuccessPayload,
  IUpdateInvoiceSortingRulesSuccessPayload,
  ApiAction,
  IInvoiceSortingRule,
} from './types';
import actions from './actions';
import {
  UpdateSettingsRequestAction,
  UpdateAdminManagerSettingsRequestAction,
  UpdateNCNSPeriodRequestAction,
  UpdateMinimumBookingTimeRequestAction,
  UpdateClockInPeriodRequestAction,
  UpdateFlaggedTimesheetsRequestAction,
  UpdateBulkJobsUploadRequestAction,
  UpdateWorkersBulkUploadRequestAction,
  UpdateBookingFulfillmentRequestAction,
  UpdateClockInOutRequestAction,
  UpdateAgencyWorkerRequestAction,
  UpdateAgenciesConfigurationsRequestAction,
  UpdateInvoiceSortingRulesRequestAction,
} from './actions.d';

const api = new ApiInstance('organization_settings');
const invoiceSortingRulesApi = new ApiInstance('organization_settings/invoice_sorting_rules');
const invoiceSortingRulesFormDataApi = new ApiInstance('organization_settings/invoice_sorting_rules/form_data');
const getAdminManagerApi = new ApiInstance('organizations/sso_protocol_settings');
const putAdminManagerApi = new ApiInstance('organizations/sso_protocol_settings/:user_type');

// Sales Demo Settings API - single endpoint for all operations
const salesDemoSettingsApi = new ApiInstance('sales_demo_settings');
const salesDemoFormDataApi = new ApiInstance('sales_demo_settings/form_data');
const salesDemoWorkersApi = new ApiInstance('sales_demo_settings/workers');

function* loadSettings(): Generator<any, void, any> {
  try {
    const state: any = yield select();
    const userRole = state.Auth.role;

    const organizationSettingsResult: IResponse<{
      organization_setting: {
        allow_admins_hours_review: boolean;
        allow_managers_hours_review: boolean;
        worker_submissions_auto_resolve_interval: number;
        worker_submissions_auto_resolve_scenario: 'none' | 'approve' | 'deny';
        edit_past_consolidated_jobs_enabled: boolean;
        invoice_sorting_rules?: IInvoiceSortingRule[];
        actions?: ApiAction[];
      };
    }> = yield call<any, IRequestParams<{}, {}>, string>(sendRequest, api.get, {}, notifications.REQUEST_ERROR);

    let salesDemoResult: IResponse<ILoadSettingsResponse> | null = null;

    if (userRole === UserRoles.SYSTEM_ADMIN) {
      salesDemoResult = yield call<any, IRequestParams<{}, {}>, string>(
        sendRequest,
        salesDemoFormDataApi.get,
        {},
        notifications.REQUEST_ERROR,
      );
    }

    const { data: salesDemoData } = salesDemoResult || {};
    const { data: organizationSettingsData } = organizationSettingsResult;

    if (
      organizationSettingsResult.status === 200 &&
      organizationSettingsData &&
      organizationSettingsData.organization_setting
    ) {
      const organizationSetting = organizationSettingsData.organization_setting;

      const organizations = (salesDemoResult?.status === 200 && salesDemoData?.organizations) || {};
      const organizationsCountries = (salesDemoResult?.status === 200 && salesDemoData?.organizations_countries) || {};
      const workers = (salesDemoResult?.status === 200 && salesDemoData?.workers) || {};

      yield put(
        actions.loadSettingsSuccess({
          allow_admins_hours_review: organizationSetting.allow_admins_hours_review,
          allow_managers_hours_review: organizationSetting.allow_managers_hours_review,
          worker_submissions_auto_resolve_scenario: organizationSetting.worker_submissions_auto_resolve_scenario,
          worker_submissions_auto_resolve_interval: organizationSetting.worker_submissions_auto_resolve_interval,
          edit_past_consolidated_jobs_enabled: organizationSetting.edit_past_consolidated_jobs_enabled,
          invoice_sorting_rules: organizationSetting.invoice_sorting_rules,
          actions: organizationSetting.actions,
          organizations,
          organizations_countries: organizationsCountries,
          workers,
        }),
      );
    } else {
      yield put(actions.loadSettingsError());

      yield put(
        notifications.addNotification({
          headline: t('dash.common.notification.error_headline'),
          type: 'error',
          message:
            organizationSettingsResult.messages?.join(', ') || t('dash.common.notification.something_went_wrong'),
        }),
      );
    }
  } catch (error) {
    logger.error(error);
  }
}

function* updateSettings({ payload }: UpdateSettingsRequestAction) {
  try {
    const result: IResponse<IUpdateSettingsResponse> = yield call<any, IRequestParams<{}, {}>, string>(
      sendRequest,
      api.put,
      { data: payload },
      notifications.REQUEST_ERROR,
    );

    if (result.status === 200) {
      yield put(actions.updateSettingsSuccess());
      yield put(
        notifications.addNotification({
          headline: t('dash.common.notification.success_headline'),
          type: 'success',
          message: t('dash.common.notification.updated_successfully', {
            item: t('dash.admin.organization.hours_approval_settings_label'),
          }),
        }),
      );
    } else {
      yield put(actions.updateSettingsError());

      yield put(
        notifications.addNotification({
          headline: t('dash.common.notification.error_headline'),
          type: 'error',
          message: t('dash.common.notification.something_went_wrong'),
        }),
      );
    }
  } catch (error) {
    logger.error(error);
  }
}

function* loadAdminManagerSettings() {
  try {
    const result: IResponse<ILoadAdminManagerSuccessPayload> = yield call<any, IRequestParams<{}, {}>, string>(
      sendRequest,
      getAdminManagerApi.get,
      {},
      notifications.REQUEST_ERROR,
    );

    const { data } = result;

    if (result.status === 200 && data) {
      yield put(actions.loadAdminManagerSettingsSuccess({ ...data }));
    } else {
      yield put(actions.loadAdminManagerSettingsError());

      yield put(
        notifications.addNotification({
          headline: t('dash.common.notification.error_headline'),
          type: 'error',
          message: t('dash.common.notification.something_went_wrong'),
        }),
      );
    }
  } catch (error) {
    logger.error(error);
  }
}

function* updateAdminManagerSettings({ payload }: UpdateAdminManagerSettingsRequestAction) {
  try {
    const { sso_protocol_setting, user_type } = payload;

    const result: IResponse<IUpdateSettingsResponse> = yield call<any, IRequestParams<{}, {}>, string>(
      sendRequest,
      putAdminManagerApi.put,
      {
        data: { sso_protocol_setting },
        pathParams: { user_type },
      },
      notifications.REQUEST_ERROR,
    );

    if (result.status === 200) {
      yield put(actions.updateAdminManagerSettingsSuccess());
      yield put(actions.loadAdminManagerSettingsRequest());
      yield put(
        notifications.addNotification({
          headline: t('dash.common.notification.success_headline'),
          type: 'success',
          message: t('dash.common.notification.updated_successfully', {
            item: t('dash.admin.organization.sso_authentication_label'),
          }),
        }),
      );
    } else {
      yield put(actions.updateAdminManagerSettingsError());

      yield put(
        notifications.addNotification({
          headline: t('dash.common.notification.error_headline'),
          type: 'error',
          message: t('dash.common.notification.something_went_wrong'),
        }),
      );
    }
  } catch (error) {
    logger.error(error);
  }
}

// Global Configurations Sagas
function* updateNCNSPeriod({ payload }: UpdateNCNSPeriodRequestAction) {
  try {
    const salesDemoPayload: IUpdateSalesDemoSettingsRequestPayload = {
      organization: {
        ncns_period: payload.ncns_period,
      },
    };

    const result: IResponse<IUpdateSettingsResponse> = yield call<any, IRequestParams<{}, {}>, string>(
      sendRequest,
      salesDemoSettingsApi.put,
      { data: salesDemoPayload },
      notifications.REQUEST_ERROR,
    );

    if (result.status === 200) {
      yield put(actions.updateNCNSPeriodSuccess());
      yield put(
        notifications.addNotification({
          headline: t('dash.common.notification.success_headline'),
          type: 'success',
          message: t('dash.common.notification.updated_successfully', {
            item: t('dash.admin.organization.ncns_period_title'),
          }),
        }),
      );
    } else {
      yield put(actions.updateNCNSPeriodError());
      yield put(
        notifications.addNotification({
          headline: t('dash.common.notification.error_headline'),
          type: 'error',
          message: t('dash.common.notification.something_went_wrong'),
        }),
      );
    }
  } catch (error) {
    logger.error(error);
  }
}

function* updateMinimumBookingTime({ payload }: UpdateMinimumBookingTimeRequestAction) {
  try {
    const salesDemoPayload: IUpdateSalesDemoSettingsRequestPayload = {
      organization: {
        organizations_countries_attributes: {
          min_allowed_time_to_book: payload.min_allowed_time_to_book,
        },
      },
    };

    const result: IResponse<IUpdateSettingsResponse> = yield call<any, IRequestParams<{}, {}>, string>(
      sendRequest,
      salesDemoSettingsApi.put,
      { data: salesDemoPayload },
      notifications.REQUEST_ERROR,
    );

    if (result.status === 200) {
      yield put(actions.updateMinimumBookingTimeSuccess());
      yield put(
        notifications.addNotification({
          headline: t('dash.common.notification.success_headline'),
          type: 'success',
          message: t('dash.common.notification.updated_successfully', {
            item: t('dash.admin.organization.minimum_booking_time_title'),
          }),
        }),
      );
    } else {
      yield put(actions.updateMinimumBookingTimeError());
      yield put(
        notifications.addNotification({
          headline: t('dash.common.notification.error_headline'),
          type: 'error',
          message: t('dash.common.notification.something_went_wrong'),
        }),
      );
    }
  } catch (error) {
    logger.error(error);
  }
}

function* updateClockInPeriod({ payload }: UpdateClockInPeriodRequestAction) {
  try {
    const salesDemoPayload: IUpdateSalesDemoSettingsRequestPayload = {
      organization: {
        organizations_countries_attributes: {
          grace_period: {
            before_clock_in: payload.before_clock_in,
          },
        },
      },
    };

    const result: IResponse<IUpdateSettingsResponse> = yield call<any, IRequestParams<{}, {}>, string>(
      sendRequest,
      salesDemoSettingsApi.put,
      { data: salesDemoPayload },
      notifications.REQUEST_ERROR,
    );

    if (result.status === 200) {
      yield put(actions.updateClockInPeriodSuccess());
      yield put(
        notifications.addNotification({
          headline: t('dash.common.notification.success_headline'),
          type: 'success',
          message: t('dash.common.notification.updated_successfully', {
            item: t('dash.admin.organization.clock_in_period_title'),
          }),
        }),
      );
    } else {
      yield put(actions.updateClockInPeriodError());
      yield put(
        notifications.addNotification({
          headline: t('dash.common.notification.error_headline'),
          type: 'error',
          message: t('dash.common.notification.something_went_wrong'),
        }),
      );
    }
  } catch (error) {
    logger.error(error);
  }
}

function* updateFlaggedTimesheets({ payload }: UpdateFlaggedTimesheetsRequestAction) {
  try {
    const salesDemoPayload: IUpdateSalesDemoSettingsRequestPayload = {
      organization: {
        organizations_countries_attributes: {
          csv_booking_filling_enabled: payload.csv_booking_filling_enabled,
        },
      },
    };

    const result: IResponse<IUpdateSettingsResponse> = yield call<any, IRequestParams<{}, {}>, string>(
      sendRequest,
      salesDemoSettingsApi.put,
      { data: salesDemoPayload },
      notifications.REQUEST_ERROR,
    );

    if (result.status === 200) {
      yield put(actions.updateFlaggedTimesheetsSuccess());
      yield put(
        notifications.addNotification({
          headline: t('dash.common.notification.success_headline'),
          type: 'success',
          message: t('dash.common.notification.updated_successfully', {
            item: t('dash.admin.organization.flagged_timesheets_title'),
          }),
        }),
      );
    } else {
      yield put(actions.updateFlaggedTimesheetsError());
      yield put(
        notifications.addNotification({
          headline: t('dash.common.notification.error_headline'),
          type: 'error',
          message: t('dash.common.notification.something_went_wrong'),
        }),
      );
    }
  } catch (error) {
    logger.error(error);
  }
}

function* updateBulkJobsUpload({ payload }: UpdateBulkJobsUploadRequestAction) {
  try {
    const salesDemoPayload: IUpdateSalesDemoSettingsRequestPayload = {
      organization: {
        organizations_countries_attributes: {
          csv_booking_filling_enabled: payload.csv_booking_filling_enabled,
        },
      },
    };

    const result: IResponse<IUpdateSettingsResponse> = yield call<any, IRequestParams<{}, {}>, string>(
      sendRequest,
      salesDemoSettingsApi.put,
      { data: salesDemoPayload },
      notifications.REQUEST_ERROR,
    );

    if (result.status === 200) {
      yield put(actions.updateBulkJobsUploadSuccess());
      yield put(
        notifications.addNotification({
          headline: t('dash.common.notification.success_headline'),
          type: 'success',
          message: t('dash.common.notification.updated_successfully', {
            item: t('dash.admin.organization.bulk_jobs_upload_title'),
          }),
        }),
      );
    } else {
      yield put(actions.updateBulkJobsUploadError());
      yield put(
        notifications.addNotification({
          headline: t('dash.common.notification.error_headline'),
          type: 'error',
          message: t('dash.common.notification.something_went_wrong'),
        }),
      );
    }
  } catch (error) {
    logger.error(error);
  }
}

function* updateWorkersBulkUpload({ payload }: UpdateWorkersBulkUploadRequestAction) {
  try {
    const salesDemoPayload: IUpdateSalesDemoSettingsRequestPayload = {
      organization: {
        organizations_countries_attributes: {
          workers_bulk_upload_enabled: payload.workers_bulk_upload_enabled,
        },
      },
    };

    const result: IResponse<IUpdateSettingsResponse> = yield call<any, IRequestParams<{}, {}>, string>(
      sendRequest,
      salesDemoSettingsApi.put,
      { data: salesDemoPayload },
      notifications.REQUEST_ERROR,
    );

    if (result.status === 200) {
      yield put(actions.updateWorkersBulkUploadSuccess());
      yield put(
        notifications.addNotification({
          headline: t('dash.common.notification.success_headline'),
          type: 'success',
          message: t('dash.common.notification.updated_successfully', {
            item: t('dash.admin.organization.workers_bulk_upload_title'),
          }),
        }),
      );
    } else {
      yield put(actions.updateWorkersBulkUploadError());
      yield put(
        notifications.addNotification({
          headline: t('dash.common.notification.error_headline'),
          type: 'error',
          message: t('dash.common.notification.something_went_wrong'),
        }),
      );
    }
  } catch (error) {
    logger.error(error);
  }
}

// Manager Settings Sagas
function* updateBookingFulfillment({ payload }: UpdateBookingFulfillmentRequestAction) {
  try {
    const salesDemoPayload: IUpdateSalesDemoSettingsRequestPayload = {
      organization: {
        organizations_countries_attributes: {
          ...(payload.request_specific_agency_enabled !== undefined && {
            request_specific_agency_enabled: payload.request_specific_agency_enabled,
          }),
          ...(payload.request_specific_talent_enabled !== undefined && {
            request_specific_talent_enabled: payload.request_specific_talent_enabled,
          }),
        },
      },
    };

    const result: IResponse<IUpdateSettingsResponse> = yield call<any, IRequestParams<{}, {}>, string>(
      sendRequest,
      salesDemoSettingsApi.put,
      { data: salesDemoPayload },
      notifications.REQUEST_ERROR,
    );

    if (result.status === 200) {
      yield put(actions.updateBookingFulfillmentSuccess());
      yield put(
        notifications.addNotification({
          headline: t('dash.common.notification.success_headline'),
          type: 'success',
          message: t('dash.common.notification.updated_successfully', {
            item: t('dash.admin.organization.booking_fulfillment_title'),
          }),
        }),
      );
    } else {
      yield put(actions.updateBookingFulfillmentError());
      yield put(
        notifications.addNotification({
          headline: t('dash.common.notification.error_headline'),
          type: 'error',
          message: t('dash.common.notification.something_went_wrong'),
        }),
      );
    }
  } catch (error) {
    logger.error(error);
  }
}

function* updateClockInOut({ payload }: UpdateClockInOutRequestAction) {
  try {
    const salesDemoPayload: IUpdateSalesDemoSettingsRequestPayload = {
      organization: {
        organizations_countries_attributes: {
          allow_managers_to_manually_clock_in_clock_out_enabled:
            payload.allow_managers_to_manually_clock_in_clock_out_enabled,
        },
      },
    };

    const result: IResponse<IUpdateSettingsResponse> = yield call<any, IRequestParams<{}, {}>, string>(
      sendRequest,
      salesDemoSettingsApi.put,
      { data: salesDemoPayload },
      notifications.REQUEST_ERROR,
    );

    if (result.status === 200) {
      yield put(actions.updateClockInOutSuccess());
      yield put(
        notifications.addNotification({
          headline: t('dash.common.notification.success_headline'),
          type: 'success',
          message: t('dash.common.notification.updated_successfully', {
            item: t('dash.admin.organization.clock_in_out_title'),
          }),
        }),
      );
    } else {
      yield put(actions.updateClockInOutError());
      yield put(
        notifications.addNotification({
          headline: t('dash.common.notification.error_headline'),
          type: 'error',
          message: t('dash.common.notification.something_went_wrong'),
        }),
      );
    }
  } catch (error) {
    logger.error(error);
  }
}

function* updateAgenciesConfigurations({ payload }: UpdateAgenciesConfigurationsRequestAction) {
  try {
    const result: IResponse<IUpdateSettingsResponse> = yield call<any, IRequestParams<{}, {}>, string>(
      sendRequest,
      api.put,
      { data: payload },
      notifications.REQUEST_ERROR,
    );

    if (result.status === 200) {
      yield put(actions.updateAgenciesConfigurationsSuccess());
      yield put(
        notifications.addNotification({
          headline: t('dash.common.notification.success_headline'),
          type: 'success',
          message: t('dash.common.notification.updated_successfully', {
            item: t('dash.admin.organization.agencies_configurations_title'),
          }),
        }),
      );
    } else {
      yield put(actions.updateAgenciesConfigurationsError());
      yield put(
        notifications.addNotification({
          headline: t('dash.common.notification.error_headline'),
          type: 'error',
          message: t('dash.common.notification.something_went_wrong'),
        }),
      );
    }
  } catch (error) {
    logger.error(error);
  }
}

// Invoices Configuration Sagas
export function* loadInvoiceSortingRulesFormData() {
  try {
    const result: IResponse<ILoadInvoiceSortingRulesFormDataSuccessPayload> = yield call<
      any,
      IRequestParams<{}, {}>,
      string
    >(sendRequest, invoiceSortingRulesFormDataApi.get, {}, notifications.REQUEST_ERROR);

    const { data } = result || {};

    if (result?.status === 200 && data) {
      yield put(actions.loadInvoiceSortingRulesFormDataSuccess({ ...data }));
    } else {
      yield put(actions.loadInvoiceSortingRulesFormDataError());
      yield put(
        notifications.addNotification({
          headline: t('dash.common.notification.error_headline'),
          type: 'error',
          message: t('dash.common.notification.something_went_wrong'),
        }),
      );
    }
  } catch (error) {
    logger.error(error);
  }
}

export function* updateInvoiceSortingRules({ payload }: UpdateInvoiceSortingRulesRequestAction) {
  try {
    const result: IResponse<IUpdateInvoiceSortingRulesSuccessPayload> = yield call<any, IRequestParams<{}, {}>, string>(
      sendRequest,
      invoiceSortingRulesApi.put,
      { data: payload },
      notifications.REQUEST_ERROR,
    );

    const { data } = result || {};

    if (result?.status === 200 && data) {
      yield put(actions.updateInvoiceSortingRulesSuccess({ ...data }));
      yield put(
        notifications.addNotification({
          headline: t('dash.common.notification.success_headline'),
          type: 'success',
          message: t('dash.common.notification.updated_successfully', {
            item: t('dash.admin.organization.invoices_configuration.global_invoices_sorting_title'),
          }),
        }),
      );
    } else {
      yield put(actions.updateInvoiceSortingRulesError());
      yield put(
        notifications.addNotification({
          headline: t('dash.common.notification.error_headline'),
          type: 'error',
          message:
            result?.validationMessages?.join(', ') ||
            result?.messages?.join(', ') ||
            t('dash.common.notification.something_went_wrong'),
        }),
      );
    }
  } catch (error) {
    logger.error(error);
  }
}

// Agency Worker Configuration Saga
function* updateAgencyWorker({ payload }: UpdateAgencyWorkerRequestAction) {
  try {
    const result: IResponse<IUpdateSettingsResponse> = yield call<any, IRequestParams<{}, {}>, string>(
      sendRequest,
      salesDemoWorkersApi.put,
      { data: payload },
      notifications.REQUEST_ERROR,
    );

    if (result.status === 200) {
      yield put(actions.updateAgencyWorkerSuccess());
      yield put(
        notifications.addNotification({
          headline: t('dash.common.notification.success_headline'),
          type: 'success',
          message: t('dash.common.notification.updated_successfully', {
            item: t('dash.admin.organization.agency_worker_configuration_title'),
          }),
        }),
      );
    } else {
      yield put(actions.updateAgencyWorkerError());
      yield put(
        notifications.addNotification({
          headline: t('dash.common.notification.error_headline'),
          type: 'error',
          message: t('dash.common.notification.something_went_wrong'),
        }),
      );
    }
  } catch (error) {
    logger.error(error);
  }
}

export default function* rootSaga() {
  yield all([takeEvery(actionTypes.LOAD_REQUEST, loadSettings)]);
  yield all([takeEvery(actionTypes.UPDATE_REQUEST, updateSettings)]);
  yield all([takeEvery(actionTypes.LOAD_ADMIN_MANAGER_REQUEST, loadAdminManagerSettings)]);
  yield all([takeEvery(actionTypes.UPDATE_ADMIN_MANAGER_REQUEST, updateAdminManagerSettings)]);
  // Global Configurations
  yield all([takeEvery(actionTypes.UPDATE_NCNS_PERIOD_REQUEST, updateNCNSPeriod)]);
  yield all([takeEvery(actionTypes.UPDATE_MINIMUM_BOOKING_TIME_REQUEST, updateMinimumBookingTime)]);
  yield all([takeEvery(actionTypes.UPDATE_CLOCK_IN_PERIOD_REQUEST, updateClockInPeriod)]);
  yield all([takeEvery(actionTypes.UPDATE_FLAGGED_TIMESHEETS_REQUEST, updateFlaggedTimesheets)]);
  yield all([takeEvery(actionTypes.UPDATE_BULK_JOBS_UPLOAD_REQUEST, updateBulkJobsUpload)]);
  yield all([takeEvery(actionTypes.UPDATE_WORKERS_BULK_UPLOAD_REQUEST, updateWorkersBulkUpload)]);
  // Manager Settings
  yield all([takeEvery(actionTypes.UPDATE_BOOKING_FULFILLMENT_REQUEST, updateBookingFulfillment)]);
  yield all([takeEvery(actionTypes.UPDATE_CLOCK_IN_OUT_REQUEST, updateClockInOut)]);
  // Agencies Configurations
  yield all([takeEvery(actionTypes.UPDATE_AGENCIES_CONFIGURATIONS_REQUEST, updateAgenciesConfigurations)]);
  // Invoices Configuration
  yield all([takeLatest(actionTypes.LOAD_INVOICE_SORTING_RULES_FORM_DATA_REQUEST, loadInvoiceSortingRulesFormData)]);
  yield all([takeLatest(actionTypes.UPDATE_INVOICE_SORTING_RULES_REQUEST, updateInvoiceSortingRules)]);
  // Agency Worker Configuration
  yield all([takeEvery(actionTypes.UPDATE_AGENCY_WORKER_REQUEST, updateAgencyWorker)]);
}
